import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { useChatUnreadCount } from '@/hooks/useChatUnreadCount';
import { useAppAuth } from '@/hooks/useAppAuth';

interface TabConfig {
  key: string;
  label: string;
  icon: string;
  iconFill: string;
  path: string;
  roles: ('patient' | 'caregiver')[];
}

const allTabs: TabConfig[] = [
  { key: 'home', label: '홈', icon: 'ri-home-4-line', iconFill: 'ri-home-4-fill', path: '/app', roles: ['patient', 'caregiver'] },
  { key: 'find-caregiver', label: '간병인찾기', icon: 'ri-search-line', iconFill: 'ri-search-fill', path: '/app/patient/search', roles: ['patient'] },
  { key: 'find-patient', label: '환자찾기', icon: 'ri-user-search-line', iconFill: 'ri-user-search-fill', path: '/app/caregiver/search', roles: ['caregiver'] },
  { key: 'chat', label: '채팅', icon: 'ri-chat-3-line', iconFill: 'ri-chat-3-fill', path: '/app/chat-list', roles: ['patient', 'caregiver'] },
  { key: 'matching', label: '매칭', icon: 'ri-file-list-3-line', iconFill: 'ri-file-list-3-fill', path: '/app/matching', roles: ['patient', 'caregiver'] },
  { key: 'profile', label: '내정보', icon: 'ri-user-3-line', iconFill: 'ri-user-3-fill', path: '/app/profile', roles: ['patient', 'caregiver'] },
];

function isActive(path: string, currentPath: string): boolean {
  if (path === '/app') return currentPath === '/app';
  return currentPath.startsWith(path);
}

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { unreadCount } = useNotifications();
  const { roles, userId } = useAppAuth(false);
  const chatUnreadCount = useChatUnreadCount(userId);

  const tabs = allTabs.filter((tab) => {
    if (roles.length === 0) return true; // roles not yet loaded, show all
    return tab.roles.some((r) => roles.includes(r));
  });

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="max-w-md mx-auto flex items-center justify-around h-16 px-2">
        {tabs.map((tab) => {
          const active = isActive(tab.path, currentPath);
          return (
            <button
              key={tab.key}
              onClick={() => navigate(tab.path)}
              className="flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 cursor-pointer relative"
            >
              <div className="w-6 h-6 flex items-center justify-center relative">
                <i className={`${active ? tab.iconFill : tab.icon} text-lg ${active ? 'text-[#2E7D6B]' : 'text-gray-400'}`} />
                {tab.key === 'matching' && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                {tab.key === 'chat' && chatUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-medium whitespace-nowrap ${active ? 'text-[#2E7D6B]' : 'text-gray-400'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}